import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  ReferenceLine,
} from "recharts";
import { AlertCircle, Loader2, Plus } from "lucide-react";
import { ACCENT, fmtDate, Field, HistoryTable } from "./shared";

/**
 * Modified Rankin Scale (mRS) — 0–6 global disability outcome after stroke.
 * Standard trial "good outcome" = mRS 0–2. Backend at
 * POST /api/specialties/neurology/mrs and /scores/mrs.
 */
const MRS_OPTIONS = [
  { score: 0, label: "0 — No symptoms" },
  { score: 1, label: "1 — No significant disability" },
  { score: 2, label: "2 — Slight disability" },
  { score: 3, label: "3 — Moderate disability" },
  { score: 4, label: "4 — Moderately severe disability" },
  { score: 5, label: "5 — Severe disability" },
  { score: 6, label: "6 — Deceased" },
];

const BAND_COLOR = {
  Excellent: "#4ADE80",
  Good: "#84CC16",
  Moderate: "#FBBF24",
  Poor: "#F87171",
  Death: "#7A8480",
};

export default function MrsTab({ data, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    score: 2,
    day_from_stroke: 90,
    notes: "",
  });
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function livePreview(score) {
    try {
      const { data: r } = await api.post("/specialties/neurology/scores/mrs", { score: Number(score) });
      setPreview(r);
    } catch { /* ignore */ }
  }

  function setScore(v) {
    setForm({ ...form, score: Number(v) });
    livePreview(v);
  }

  async function submit(e) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/specialties/neurology/mrs", {
        date: form.date,
        score: Number(form.score),
        day_from_stroke: form.day_from_stroke ? Number(form.day_from_stroke) : null,
        notes: form.notes || null,
      });
      toast.success(`mRS ${form.score} recorded`);
      setPreview(null);
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save mRS");
    } finally { setSaving(false); }
  }

  const records = data?.records || [];
  const chartData = useMemo(
    () => [...records].slice().reverse().map((r) => ({ date: fmtDate(r.date), mRS: r.score })),
    [records]
  );
  const bg = preview ? BAND_COLOR[preview.outcome_band] || "#A3ACA7" : "#A3ACA7";

  return (
    <div className="space-y-6" data-testid="neuro-mrs-tab">
      <form onSubmit={submit} className="ros-card space-y-4" data-testid="mrs-form">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading text-lg">Modified Rankin Scale</div>
            <div className="text-xs text-[#A3ACA7] mt-0.5">
              Day-90 functional outcome after stroke. Van Swieten 1988 · Rankin 1957.
            </div>
          </div>
          {preview && (
            <div className="text-right" data-testid="mrs-preview">
              <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">Outcome band</div>
              <div className="text-lg font-heading font-bold" style={{ color: bg }}>
                {preview.outcome_band}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Assessment date">
            <input type="date" value={form.date}
                   onChange={(e) => setForm({ ...form, date: e.target.value })}
                   data-testid="mrs-input-date" className="ros-input" required />
          </Field>
          <Field label="Day post-stroke">
            <input type="number" min={0} max={730} value={form.day_from_stroke}
                   onChange={(e) => setForm({ ...form, day_from_stroke: e.target.value })}
                   data-testid="mrs-input-day" className="ros-input" placeholder="e.g. 90" />
          </Field>
          <Field label="mRS score">
            <select value={form.score} onChange={(e) => setScore(e.target.value)}
                    data-testid="mrs-input-score" className="ros-input">
              {MRS_OPTIONS.map((o) => (
                <option key={o.score} value={o.score}>{o.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Notes">
          <textarea rows={2} value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    data-testid="mrs-input-notes"
                    className="ros-input" placeholder="Rehab status, functional context, next-of-kin discussion…" />
        </Field>

        {preview && (
          <div className="rounded-2xl p-4" style={{ background: `${bg}12`, border: `1px solid ${bg}40` }}
               data-testid="mrs-recommendation">
            <div className="text-white font-semibold">{preview.label}</div>
            <div className="text-xs text-[#A3ACA7] mt-1">{preview.description}</div>
            <div className="text-sm text-white mt-2">{preview.recommendation}</div>
          </div>
        )}

        {error && (
          <div className="text-sm text-[#F87171] flex items-center gap-2" data-testid="mrs-error">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div>
          <button type="submit" disabled={saving} data-testid="mrs-submit"
                  className="flex items-center gap-2 px-5 py-2 rounded-full text-black font-semibold disabled:opacity-50 transition-colors"
                  style={{ background: ACCENT }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Saving…" : "Save mRS assessment"}
          </button>
        </div>
      </form>

      {chartData.length > 0 && (
        <div className="ros-card" data-testid="mrs-chart">
          <div className="font-heading text-lg mb-3">mRS trajectory</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 16, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#272E2B" />
                <XAxis dataKey="date" stroke="#A3ACA7" fontSize={11} />
                <YAxis stroke="#A3ACA7" fontSize={11} domain={[0, 6]} reversed />
                <Tooltip contentStyle={{ background: "#0F1312", border: "1px solid #272E2B", borderRadius: 12 }} />
                <ReferenceLine y={2} stroke="#FBBF24" strokeDasharray="4 4"
                               label={{ value: "Good outcome ≤2", fontSize: 10, fill: "#FBBF24" }} />
                <Line type="monotone" dataKey="mRS" stroke={ACCENT} strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <HistoryTable
        title={`mRS history (${records.length})`}
        testId="mrs-history"
        rows={records}
        rowTestId={(r) => `mrs-row-${r.id}`}
        empty="No mRS assessments recorded yet."
        columns={[
          { label: "Date", render: (r) => fmtDate(r.date) },
          { label: "Score", render: (r) => <span className="font-mono">{r.score}</span> },
          { label: "Band", render: (r) => (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ color: BAND_COLOR[r.outcome_band],
                           background: `${BAND_COLOR[r.outcome_band]}18`,
                           border: `1px solid ${BAND_COLOR[r.outcome_band]}40` }}>
              {r.outcome_band}
            </span>
          )},
          { label: "Day post-stroke", render: (r) => (
            <span className="font-mono text-[#A3ACA7]">{r.day_from_stroke ?? "—"}</span>
          )},
          { label: "Recommendation", render: (r) => <span className="text-xs text-[#A3ACA7]">{r.recommendation}</span> },
        ]}
      />
    </div>
  );
}
